import { useEffect, useState } from "react";
import { api } from "./api";
import type { Membership } from "./api";
import { Badge, Icon, PageTitle, dateTime, errorMessage } from "./components";
import {
  configuredLabel,
  configuredValue,
  membershipLabel,
  subscriptionLabel,
} from "./view-models";

export function CardScreen({
  signedIn,
  personId,
  onAuth,
}: {
  signedIn: boolean;
  personId?: string;
  onAuth: () => void;
}) {
  const [membership, setMembership] = useState<Membership | null>(null);
  const [error, setError] = useState("");
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"card" | "offers" | "subscription">("card");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setMembership(null);
    setError("");
    setRequested(false);
    api
      .membership()
      .then((value) => {
        if (active) setMembership(value);
      })
      .catch((error) => {
        if (active) setError(errorMessage(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [signedIn, personId]);
  async function subscribe() {
    if (!signedIn) {
      onAuth();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.subscribe();
      setRequested(true);
      setMembership(await api.membership());
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  const intentRecorded =
    requested || membership?.subscription_intent?.status === "requested";
  return (
    <>
      <PageTitle eyebrow="Un lien qui dure" title="Ma carte">
        Votre place dans Avalon, à votre rythme.
      </PageTitle>
      <div
        className="segments segments-three"
        aria-label="Vues de la GuildCard"
      >
        <button aria-pressed={view === "card"} onClick={() => setView("card")}>
          Carte et crédits
        </button>
        <button
          aria-pressed={view === "offers"}
          onClick={() => setView("offers")}
        >
          Formules et droits
        </button>
        <button
          aria-pressed={view === "subscription"}
          onClick={() => setView("subscription")}
        >
          Mon abonnement
        </button>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {view === "card" && (
        <>
          <div className="guild-card">
            <div className="section-heading">
              <span className="wordmark">Avalon</span>
              <Icon name="spark" size={30} />
            </div>
            <div>
              <span className="eyebrow">La Guilde</span>
              <h2>
                Des lieux.
                <br />
                Des liens.
                <br />
                Des possibles.
              </h2>
            </div>
            <span className="card-status">
              {loading
                ? "Chargement du statut…"
                : membershipLabel(membership?.status, signedIn)}
            </span>
          </div>
          {membership?.demo && (
            <p className="note">
              Carte de démonstration · aucun abonnement payé
            </p>
          )}
          <p className="fineprint">
            Cette carte à l’écran n’est pas encore un titre de validation à
            l’accueil.
          </p>
          <section className="panel">
            <h2>Aller plus loin avec la GuildCard</h2>
            <p className="muted">
              Découvrez les formules, les droits et leurs conditions avant de
              commencer votre demande.
            </p>
            <button
              className="button primary full"
              onClick={() => setView("offers")}
            >
              Découvrir les formules <Icon name="arrow" />
            </button>
          </section>
          <section className="panel">
            <div className="section-heading">
              <h2>Mes crédits</h2>
              <Badge>Non connecté</Badge>
            </div>
            <p className="muted">
              Le fournisseur de crédits n’est pas encore raccordé. Aucun solde
              n’est estimé ici.
            </p>
            <p className="fineprint">
              Vos crédits, vos droits GuildCard et un éventuel abonnement sont
              suivis séparément.
            </p>
          </section>
        </>
      )}
      {view === "offers" && (
        <>
          <section className="panel">
            <Badge>Sans adhésion</Badge>
            <h2>Le compte gratuit</h2>
            <ul className="feature-list">
              <li>Choisir sa visibilité et présenter ses apports.</li>
              <li>Exprimer un besoin et demander une mise en relation.</li>
              <li>Retrouver ses demandes et répondre à son rythme.</li>
            </ul>
          </section>
          <section className="panel">
            <h2>La GuildCard</h2>
            {loading ? (
              <p className="muted">Chargement des formules…</p>
            ) : !membership ? (
              <p className="muted">
                Les formules sont momentanément indisponibles.
              </p>
            ) : membership.offers.length ? (
              <div className="stack">
                {membership.offers.map((offer) => (
                  <article className="booking-summary" key={offer.id}>
                    <div className="section-heading">
                      <h3>{offer.label}</h3>
                      <Badge>
                        {offer.status === "published" ||
                        offer.status === "active"
                          ? "Conditions publiées"
                          : "En préparation"}
                      </Badge>
                    </div>
                    <dl className="facts">
                      {Object.entries(offer.terms).map(([key, value]) => (
                        <div key={key}>
                          <dt>{configuredLabel(key)}</dt>
                          <dd>{configuredValue(value)}</dd>
                        </div>
                      ))}
                    </dl>
                    {!Object.keys(offer.terms).length && (
                      <p className="muted">
                        Les conditions détaillées restent à préciser.
                      </p>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <>
                <Badge>En préparation</Badge>
                <p className="muted">
                  Les formules, leurs prix et leurs droits supplémentaires
                  restent à préciser. Aucun tarif historique ne s’applique
                  automatiquement.
                </p>
                <dl className="facts">
                  <div>
                    <dt>Formules et prix</dt>
                    <dd>En préparation</dd>
                  </div>
                  <div>
                    <dt>Droits et quotas</dt>
                    <dd>À préciser pour chaque formule</dd>
                  </div>
                  <div>
                    <dt>Périodicité et conditions d’arrêt</dt>
                    <dd>Présentées avant toute souscription</dd>
                  </div>
                </dl>
              </>
            )}
            <button
              className="button primary full"
              onClick={() => setView("subscription")}
            >
              Commencer ma demande de GuildCard <Icon name="arrow" />
            </button>
          </section>
          {signedIn && (
            <section className="panel">
              <h2>Mes droits actuels</h2>
              {!membership ? (
                <p className="muted">
                  Les droits de votre compte sont momentanément indisponibles.
                </p>
              ) : membership.entitlements.length ? (
                <dl className="facts">
                  {membership.entitlements.map((right) => (
                    <div key={right.key}>
                      <dt>{configuredLabel(right.key)}</dt>
                      <dd>{configuredValue(right.value)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="muted">
                  Aucun droit GuildCard supplémentaire n’est attribué à ce
                  compte. Les fonctions du compte gratuit restent accessibles.
                </p>
              )}
            </section>
          )}
          <p className="fineprint">
            Les lieux et la programmation restent consultables sans compte.
          </p>
        </>
      )}
      {view === "subscription" && (
        <>
          <section className="panel">
            <h2>Ma demande de GuildCard</h2>
            <p className="muted">
              Enregistrez votre intention pour retrouver cette étape dans votre
              compte. Vous ne choisissez aucune formule et aucun paiement n’est
              engagé.
            </p>
            {loading ? (
              <p className="muted">Chargement de votre demande…</p>
            ) : intentRecorded ? (
              <>
                <p role="status" className="success">
                  Votre intention est enregistrée. Aucun abonnement ni paiement
                  n’a été engagé.
                </p>
                {membership?.subscription_intent?.created_at && (
                  <p className="fineprint">
                    Enregistrée le{" "}
                    {dateTime(
                      new Date(
                        membership.subscription_intent.created_at * 1000,
                      ).toISOString(),
                    )}
                  </p>
                )}
              </>
            ) : (
              <button
                className="button primary full"
                disabled={busy || loading || !membership}
                onClick={subscribe}
              >
                {busy
                  ? "Enregistrement…"
                  : signedIn
                    ? "Enregistrer mon intention"
                    : "Créer mon compte pour continuer"}
                <Icon name="arrow" />
              </button>
            )}
          </section>
          <section className="panel">
            <div className="section-heading">
              <h2>Mon abonnement</h2>
              <Badge>
                {loading
                  ? "Chargement…"
                  : subscriptionLabel(membership?.subscription.status)}
              </Badge>
            </div>
            <p className="muted">
              {membership?.subscription.status === "active"
                ? "Un abonnement est associé à ce compte. Sa gestion n’est pas encore raccordée à cette interface."
                : "Votre demande de GuildCard ne crée aucun abonnement. La gestion et le paiement seront accessibles après le raccordement du service de souscription."}
            </p>
            <button className="button full" disabled>
              Gestion d’abonnement indisponible
            </button>
          </section>
        </>
      )}
    </>
  );
}
